import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Paper,
  TextField,
  InputAdornment,
  Box,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
} from "@mui/material";
import {
  Search as SearchIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material";
import { BASE_URL } from "../../services/configUrls";

import FdpUpcoming from "./FdpUpcoming";
import FdpOngoing from "./FdpOngoing";
import FdpCompletion from "./FdpCompletion";

// Tab config mapped to backend status values
const STATUS_TABS = [
  { label: "Upcoming", value: "upcoming" },
  { label: "Ongoing", value: "ongoing" },
  { label: "Completed", value: "completed" },
];

const FacultyEventsPage = () => {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "upcoming";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [completedCount, setCompletedCount] = useState(null);

  // All events categorized by the backend
  const [allEvents, setAllEvents] = useState({
    upcoming: [],
    ongoing: [],
    completed: [],
  });

  useEffect(() => {
    fetchFacultyEvents();
  }, []);

  const fetchFacultyEvents = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}/event/get-faculty-events`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch faculty events");
      }

      const data = await response.json();

      setAllEvents({
        upcoming: data.upcoming || [],
        ongoing: data.ongoing || [],
        completed: data.completed || [],
      });

      // Fetch the detailed completed events to get the correct count on load
      const completedResponse = await fetch(`${BASE_URL}/event/faculty-events`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          "Content-Type": "application/json",
        },
      });

      if (completedResponse.ok) {
        const completedData = await completedResponse.json();
        const fdpCount = (completedData.fdp || []).length;
        const edpCount = (completedData.edp || []).length;
        setCompletedCount(fdpCount + edpCount);
      }
    } catch (error) {
      console.error("Error fetching faculty events:", error);
      setAllEvents({ upcoming: [], ongoing: [], completed: [] });
    } finally {
      setLoading(false);
    }
  };

  // Get the events for the current active tab
  const currentTabEvents = allEvents[activeTab] || [];

  // Filter by search term
  const filteredEvents = currentTabEvents.filter((event) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      event.event_domain_name?.toLowerCase().includes(term) ||
      event.event_type?.toLowerCase().includes(term) ||
      event.edu_name?.toLowerCase().includes(term) ||
      event.email?.toLowerCase().includes(term)
    );
  });

  const handleTabChange = (_event, newValue) => {
    setActiveTab(newValue);
    setSearchTerm("");
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* ── Page Title ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2.5,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: "#1F2937",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <CalendarIcon sx={{ color: "#3B82F6" }} />
          Events
        </Typography>
      </Box>

      {/* ── Status Tabs ── */}
      <Box sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            px: 2,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.875rem",
              minHeight: 48,
              color: "#6B7280",
              "&.Mui-selected": {
                color: "#3B82F6",
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#3B82F6",
              height: 3,
              borderRadius: "3px 3px 0 0",
            },
          }}
        >
          {STATUS_TABS.map((tab) => {
            const count = tab.value === "completed" && completedCount !== null
              ? completedCount
              : (allEvents[tab.value]?.length || 0);
            return (
              <Tab
                key={tab.value}
                label={`${tab.label} (${count})`}
                value={tab.value}
              />
            );
          })}
        </Tabs>
      </Box>

      {/* ── Search Bar ── */}
      {activeTab === "upcoming" && (
        <Paper
          sx={{
            p: 2,
            mb: 2,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            borderRadius: "12px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <TextField
              placeholder="Search events..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#9CA3AF", fontSize: "1rem" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 280,
                "& .MuiOutlinedInput-root": {},
              }}
              size="small"
            />
            <Box sx={{ flexGrow: 1 }} />
            <Typography variant="body2" sx={{ color: "#6B7280" }}>
              Total: <strong>{filteredEvents.length}</strong> events
            </Typography>
          </Box>
        </Paper>
      )}

      {/* ── Content area ── */}
      <Box sx={{ mb: 2 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 8,
            }}
          >
            <CircularProgress sx={{ color: "#3B82F6" }} />
          </Box>
        ) : (
          <>
            {activeTab === "upcoming" && (
              <FdpUpcoming events={filteredEvents} />
            )}

            {activeTab === "ongoing" && (
              <FdpOngoing events={filteredEvents} />
            )}

            {activeTab === "completed" && (
              <FdpCompletion
                events={filteredEvents}
                tabType="completed"
                onDataLoad={setCompletedCount}
              />
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default FacultyEventsPage;
